import { type CSSProperties, useEffect, useId, useRef, useState } from 'react';
import {
  data,
  Form,
  redirect,
  useActionData,
  useLoaderData,
  useNavigation,
  type RouterContextProvider,
} from 'react-router';
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'motion/react';
import { Texto, useTextos } from '@vigilancia/i18n';
import { usePreferencias } from '@vigilancia/cascaron';
import { formatearBuildInfo, leerAccesos, leerBuildInfo } from '@vigilancia/runtime';
import { leerConfiguracionDeServidor } from '@vigilancia/runtime/servidor';

import logoClaro from '@vigilancia/ui/activos/logos/natclar-full-dark.png';
import logoOscuro from '@vigilancia/ui/activos/logos/natclar-full-light.png';

import {
  CAMPO_CLAVE,
  CAMPO_DESTINO,
  CAMPO_DOCUMENTO,
  DOCUMENTO_MAXIMO,
  type ClaveDeError,
  type Rechazo,
} from '../campos';
import { leerDocumento, procesarEnvio } from '../credenciales.server';
import { elegirDestino, sanearDestino } from '../destino';
import { DIRECCION_DEL_CLIENTE } from '../entry.server';
import { rutaInterna } from '../llamada-interna.server';
import { comoLlega } from '../puerta.server';
import {
  leerUsuarioRecordado,
  loRellenoElNavegador,
  olvidarUsuario,
  recordarUsuario,
} from '../recordado';
import { borrarSesion, sellarSesion } from '../sesion.server';
import catalogo from '../../locales/es.json';

/**
 * El título del documento sale del MISMO catálogo que el resto, aunque se lea
 * directo: `meta` no es un componente y no puede usar el proveedor de idioma.
 * Lo que importa de la regla —que ningún texto se escriba dentro del código— se
 * respeta igual.
 */
export function meta() {
  return [{ title: catalogo['acceso.documento.titulo'] }];
}

/**
 * La puerta.
 *
 * Un solo camino y ninguna distracción: identificarse.
 *
 * **El usuario ES el documento.** No hay variante de correo: el identificador
 * de la gente del puesto médico y del trabajador es el documento, y ofrecer dos
 * formas de identificarse le pide a cada persona una decisión que no le toca.
 *
 * DOS AUSENCIAS DELIBERADAS, y las dos son decisiones:
 *
 * · **No hay «mantener la sesión iniciada».** La sesión dura lo que dura su
 *   testigo y no se prolonga desde aquí. ⓘ Si algún día se pide «no volver a
 *   escribir nada», la salida está estudiada y es ésta —una sesión larga y
 *   revocable—, no guardar credenciales en el navegador: ADR-0013.
 * · **No hay «crear cuenta».** Las cuentas las da la organización.
 *
 * ⚠️ **Y SÍ HAY «RECORDAR USUARIO», desde el 23/09/2026.** Hasta ese día esta
 * cabecera lo listaba como una tercera ausencia, con esta razón: «el navegador
 * de un tópico compartido no puede ser caché de nada». **La premisa era falsa
 * para este sistema** y lo dijo Alex al leerla: hay varios tipos de usuario y
 * casi ninguno se sienta en un tópico compartido —el personal administrativo y
 * los propios trabajadores entran desde SUS dispositivos, muchos desde el
 * móvil—; donde ese uso exista, corre por cuenta de quien lo hace.
 *
 * Lo que se recuerda es **el documento, y nunca la contraseña**: lo único que
 * permite no escribirla es tener la sesión abierta. Se guarda sólo si alguien
 * marca la casilla, quitarla olvida en el acto, y el número **no viaja al
 * servidor**. El trato entero —y las cuatro salidas que se estudiaron para la
 * contraseña— está en el **ADR-0013**.
 * · **No hay texto de ayuda bajo cada campo.** La etiqueta dice qué se pide y
 *   la sugerencia enseña la forma; un párrafo explicando lo que ya se entiende
 *   es ruido justo donde hace falta velocidad.
 */

/**
 * A dónde se va cuando nadie pidió un sitio · ✅ **LO DICE EL ROL desde el
 * 29/09/2026.**
 *
 * Aquí vivía una constante —`/operacion/`, «provisional: lo dirá el rol»—. Ahora
 * lo decide `elegirDestino` con los accesos que el RBAC de Natclar le da a quien
 * entra: el sitio pedido si su aplicación se tiene, si no la primera que se
 * tenga, y ninguna si no tiene ninguna. Las bases que devuelve llevan la barra
 * final, que es la forma buena: sin ella, quien se identificaba aterrizaba en un
 * 404 del servidor de archivos justo después de escribir bien su contraseña.
 */

/**
 * Quién llega a la puerta, y si tiene algo que hacer aquí.
 *
 * **QUIEN YA TIENE SESIÓN NO VUELVE A IDENTIFICARSE.** Es la otra mitad de
 * exigir la sesión: los subsistemas mandan aquí a quien no la tiene, y esta
 * pantalla devuelve a su sitio a quien sí. Sin esto, quien vuelve al día
 * siguiente con su sesión viva vería un formulario que no necesita.
 *
 * **Y LA COOKIE MUERTA SE QUITA AQUÍ, EN LA MISMA RESPUESTA QUE PINTA EL
 * FORMULARIO.** Eso es lo único que impide el bucle: sin ello, Operación
 * rebotaría aquí por el 401, esta pantalla vería una cookie y devolvería a
 * Operación, indefinidamente.
 *
 * CUANDO NO SE PUEDE PREGUNTAR NO SE BORRA NADA. Un tropiezo de la API no
 * puede cerrarle la sesión a todo el mundo; se pinta el formulario y la cookie
 * se queda donde está, por si el siguiente intento sí llega.
 *
 * EL TERMINAL COMPARTIDO SALE GANANDO, aunque parezca lo contrario. Antes,
 * quien se sentaba después de alguien que no cerró sesión veía un formulario
 * limpio mientras la sesión de la persona anterior seguía viva en el navegador.
 * Ahora ve su nombre en la cabecera y «Cerrar sesión» a un clic: la sesión
 * heredada deja de estar escondida detrás de una pantalla de acceso.
 *
 * COSTE: una llamada a la API, y solo cuando hay cookie.
 */
export async function loader({ request }: { request: Request }) {
  const url = new URL(request.url);
  const config = leerConfiguracionDeServidor(process.env);
  // Se sanea al ENTRAR y no solo al salir: así el valor que viaja en el campo
  // oculto del formulario ya está limpio, y no hay una segunda oportunidad de
  // colar un destino ajeno entre la carga y el envío.
  const destino = sanearDestino(url.searchParams.get('destino'));

  const llegada = await comoLlega(config.apiUrl, request);

  if (llegada.como === 'con-sesion') {
    const adonde = elegirDestino(destino, llegada.accesos);
    if (adonde !== null) {
      // URL ABSOLUTA, por el mismo motivo medido que en la acción: con una ruta,
      // el marco le pone delante el `basename` de la puerta y saldría
      // `/acceso/operacion/`, que no existe en ninguna parte.
      return redirect(new URL(adonde, request.url).toString());
    }

    /*
      🆕 29/09/2026 · CON SESIÓN Y SIN NINGUNA APLICACIÓN. Se dice aquí, y la
      cookie SE QUITA: una sesión que no abre nada sólo sirve para rebotar entre
      pantallas que dicen que no. Cuando Natclar le dé un rol, vuelve a entrar.
    */
    const aviso: ClaveDeError = 'acceso.error.sinAcceso';
    return data({ config, destino, aviso }, { headers: { 'Set-Cookie': borrarSesion() } });
  }

  const datos = { config, destino, aviso: null };

  return llegada.como === 'con-cookie-muerta'
    ? data(datos, { headers: { 'Set-Cookie': borrarSesion() } })
    : data(datos);
}

/**
 * Posición de un elemento en la CASCADA de entrada.
 *
 * El retardo no se escribe en milisegundos aquí: la regla `.acceso-entra`
 * multiplica este número por `--t-paso`, que es un token. Así el escalón se
 * ajusta —o se anula bajo `prefers-reduced-motion`— en un solo sitio, y el
 * componente solo dice el ORDEN, que es lo único que sabe.
 *
 * El molde va aparte porque `CSSProperties` no admite propiedades
 * personalizadas: sin el molde, TypeScript rechaza `--orden` en cada uso.
 */
function orden(posicion: number): CSSProperties {
  return { '--orden': String(posicion) } as CSSProperties;
}

/**
 * El envío del formulario.
 *
 * Cuando la API acepta, la respuesta hace DOS cosas a la vez y en una sola
 * cabecera: sella la cookie `HttpOnly` con el testigo y redirige al destino.
 * Va junto a propósito — si la cookie se pusiera en una respuesta y el redirect
 * en otra, entre las dos habría un instante con la sesión abierta y el usuario
 * todavía en la puerta.
 *
 * El destino ya venía saneado del cargador y se vuelve a sanear aquí, porque
 * entre pintar el formulario y enviarlo el campo oculto puede haber cambiado:
 * lo que se pinta no es lo que se recibe.
 *
 * **ESTA ACCIÓN NO PUEDE LANZAR NADA QUE SE PUEDA PREVER**, y por eso los dos
 * `try` de abajo, que antes no estaban. Lo que sale de aquí sin capturar no
 * repinta el formulario: se lleva la pantalla entera y la sustituye por el
 * último recurso de `root.tsx`, sin campos, sin mensaje útil y sin forma de
 * reintentar. Un fallo que se puede nombrar tiene que volver como aviso dentro
 * de la puerta; el último recurso es para lo que no supimos prever.
 */
export async function action({
  request,
  context,
}: {
  request: Request;
  context: RouterContextProvider;
}): Promise<Response | Rechazo> {
  const config = leerConfiguracionDeServidor(process.env);

  // `formData()` LANZA con un cuerpo que no se puede analizar —un envío
  // truncado, un tipo de contenido que no corresponde—. No es un usuario
  // equivocándose, pero tampoco es motivo para tirar la pantalla: se responde
  // lo único que resuelve ese caso, que es volver a cargar y reintentar.
  let campos: FormData;
  try {
    campos = await request.formData();
  } catch {
    return { mensaje: 'acceso.error.envioIlegible', campos: [], documento: '' };
  }

  // LA DIRECCIÓN LA RESUELVE EL SERVIDOR Y BAJA POR EL CONTEXTO, igual que el
  // nonce. La cadena vacía significa «no se sabe» —desarrollo sin proxy
  // delante— y la API cuenta entonces con lo que ella vea: sin dato no se
  // inventa uno.
  const direccion = context.get(DIRECCION_DEL_CLIENTE);

  const resultado = await procesarEnvio(campos, {
    apiUrl: config.apiUrl,
    interna: await rutaInterna(),
    origen: direccion === '' ? null : direccion,
  });

  if (!resultado.ok) return resultado.valor;

  /*
    🆕 29/09/2026 · ADÓNDE, LO DICE SU RBAC. El sitio pedido si su aplicación se
    tiene; si no, la primera que se tenga. Y si no tiene NINGUNA, no se sella la
    cookie: una sesión que no abre nada sólo sirve para rebotar. Se le dice aquí
    mismo, sin marcar ningún campo, porque sus datos eran buenos.
  */
  const adonde = elegirDestino(
    resultado.valor.destino,
    leerAccesos(resultado.valor.sesion.accesos),
  );
  if (adonde === null) {
    return { mensaje: 'acceso.error.sinAcceso', campos: [], documento: leerDocumento(campos) };
  }

  // EL DESTINO ESTÁ FUERA DE ESTA APLICACIÓN, y hay que decirlo con una URL
  // absoluta. Con una ruta, el marco le pone delante el `basename` de la
  // puerta —`/acceso/operacion`, que no existe en ninguna parte— porque para
  // él toda ruta es suya. Medido: con `/operacion` la cabecera salía como
  // `/acceso/operacion`.
  //
  // El origen sale de la propia petición, así que sirve igual en desarrollo
  // detrás de la pasarela y en producción detrás del balanceador, sin que
  // ninguna dirección esté escrita en el código. El CAMINO ya viene saneado
  // contra la lista de subsistemas, que es lo que impide que esto sea un
  // trampolín a otro sitio.
  const destino = new URL(adonde, request.url);

  // SELLAR PUEDE FALLAR, Y FALLARÍA EN EL PEOR MOMENTO: con las credenciales
  // ya verificadas. `sellarSesion` lanza si el testigo trae caracteres que no
  // caben en una cookie sin escapar, y eso significa que el contrato de la API
  // cambió por debajo — la misma clase de fallo que «la respuesta no cumple el
  // contrato», y se cuenta igual.
  //
  // Se registra el HECHO y nunca el testigo: el motivo entero de que esto
  // exista es que ese valor abre una sesión.
  let cookie: string;
  try {
    cookie = sellarSesion(resultado.valor.sesion);
  } catch {
    // LA ÚNICA CONSOLA DE LA INTERFAZ, y va con su motivo escrito. La regla que
    // la prohíbe existe porque un `console.log` olvidado en una pantalla
    // clínica acaba imprimiendo el dato de una persona. Aquí no hay dato:
    // esta rama solo corre en el servidor, el mensaje es una constante, y sin
    // ella el síntoma sería que TODO EL MUNDO deja de poder entrar sin que
    // ningún registro diga por qué. En Cloud Run la salida estándar es el
    // registro, y `severity` es lo que la clasifica.
    // eslint-disable-next-line no-console
    console.error(
      JSON.stringify({
        severity: 'ERROR',
        message: 'El testigo de sesión no cabe en una cookie sin escapar.',
      }),
    );
    return {
      mensaje: 'acceso.error.servicioNoDisponible',
      campos: [],
      documento: leerDocumento(campos),
    };
  }

  return redirect(destino.toString(), { headers: { 'Set-Cookie': cookie } });
}

/**
 * Los tres pasos del sistema, en el orden en que ocurren de verdad: quién entra
 * a un programa, qué se le controla, y qué pasa cuando algo aparece. Salen del
 * TO BE —`DF-VIG-003/005`, `004/006/007` y `009`—, no de una lista de virtudes.
 */
const PUNTOS = [
  { clave: 'punto1', color: 'var(--n-celeste)' },
  { clave: 'punto2', color: 'var(--n-lavanda)' },
  { clave: 'punto3', color: 'var(--n-melon)' },
] as const;

/**
 * Los cuatro glifos de los conmutadores, **copiados del muestrario del sistema
 * de diseño**, que es donde viven hoy los pictogramas de interfaz.
 *
 * Antes eran CARACTERES DE TEXTO —`☾ ☀ ◐ ●`— y eso fue un error de tipo, no de
 * gusto: un carácter depende de que la fuente del sistema lo tenga. Medido en
 * una captura sin fuentes de escritorio, la luna simplemente NO SE DIBUJÓ y el
 * botón salió vacío: un control sin nada dentro, imposible de interpretar. Un
 * glifo de interfaz es geometría, y la geometría se declara.
 *
 * DEUDA DECLARADA: el juego de pictogramas de interfaz del sistema vive inline
 * en el muestrario y se copia en las aplicaciones —`i-ojo` y `i-alerta` ya
 * estaban duplicados aquí antes que éstos—. Debería ser un activo compartido,
 * como el trazo de la marca. No se resuelve en este cambio porque toca el
 * muestrario entero y sus `<use href>`.
 */
const GLIFOS = {
  sol: (
    <>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6" />
    </>
  ),
  luna: <path d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4z" />,
  vidrio: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M8 20 20 8M4 15l11-11" />
    </>
  ),
  solido: <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="currentColor" stroke="none" />,
} as const;

export default function Entrar() {
  const { config, destino, aviso } = useLoaderData<typeof loader>();
  const rechazo = useActionData<typeof action>();
  // Lo que dice el formulario: lo del último envío, o lo que el cargador supo al
  // llegar —hoy, sólo que la sesión no abre ninguna aplicación—.
  const mensaje: ClaveDeError | null = rechazo?.mensaje ?? aviso;
  const navegacion = useNavigation();
  const { tema, vidrio, alternarTema, alternarVidrio } = usePreferencias();
  const sinMovimiento = useReducedMotion();
  const t = useTextos();
  const build = leerBuildInfo();

  const idDocumento = useId();
  const idClave = useId();
  const idMensaje = useId();
  const [claveVisible, setClaveVisible] = useState(false);

  /*
    «RECORDAR USUARIO» · SIN ESTADO DE REACT, Y ES DELIBERADO.

    Nadie más lee la casilla: ni pinta nada distinto, ni habilita nada. Su
    única verdad es el DOM, igual que la del campo del documento de aquí al
    lado, que también va sin control. Con estado habría que decidir qué rinde
    el servidor —que **no sabe** si este navegador recuerda a alguien, porque el
    documento vive sólo aquí— y marcarla al hidratar sería un repintado en
    cascada por una comodidad.

    Se rinde apagada y la marca llega al hidratar, escrita sobre el nodo.
  */
  const casillaRecordar = useRef<HTMLInputElement>(null);
  const campoDocumento = useRef<HTMLInputElement>(null);

  /*
    ¿EL CAMBIO DE GLIFO LO PIDIÓ UNA PERSONA? Hasta que alguien pulse, no.

    El servidor rinde siempre el tema claro y la preferencia real llega al
    hidratar, así que en cada carga en oscuro el glifo cambia de luna a sol sin
    que nadie haya tocado nada. Con `mode="wait"` eso deja el botón VACÍO
    mientras uno sale y el otro entra — medido en captura: unos 200 ms de botón
    en blanco en cada carga, y encima animados, que es justo el parpadeo que
    esta pantalla evita en todo lo demás.

    Con este interruptor el intercambio de la hidratación es instantáneo y el
    del clic gira. Es la regla que ya estaba escrita en ADR-0009 —solo se anima
    lo que responde a una acción—; aquí se hace cumplir.
  */
  const [conmutado, setConmutado] = useState(false);

  const enviando = navegacion.state === 'submitting';

  /**
   * VOLVER ATRÁS NO DEBE DEVOLVER UN BOTÓN ATASCADO.
   *
   * El navegador guarda la página entera —incluido el estado de JavaScript— al
   * salir de ella, y la restaura tal cual al pulsar atrás. Como el envío se
   * quedó a medias cuando la navegación se lo llevó, el marco sigue creyendo
   * que está enviando: el botón vuelve diciendo «Verificando…», deshabilitado,
   * y no hay forma de salir de ahí porque esa navegación nunca va a terminar.
   *
   * `pageshow` con `persisted` es la única señal que distingue «vengo de la
   * caché de atrás» de una carga normal. Recargar es lo que devuelve el
   * formulario a un estado limpio; intentar arreglar el estado a mano dejaría
   * al marco creyendo otra cosa que la pantalla.
   */
  useEffect(() => {
    const alRestaurar = (evento: PageTransitionEvent): void => {
      if (evento.persisted) window.location.reload();
    };
    window.addEventListener('pageshow', alRestaurar);
    return () => {
      window.removeEventListener('pageshow', alRestaurar);
    };
  }, []);

  /**
   * EL USUARIO RECORDADO VUELVE AL CAMPO, y sin pisar a nadie.
   *
   * Corre una vez, al hidratar, porque es cuando existe el almacenamiento del
   * navegador. La casilla se marca SIEMPRE que haya alguien recordado —dice la
   * verdad de lo que hay guardado—; el campo, en cambio, sólo se rellena si
   * está libre:
   *
   * · **si ya trae algo, manda lo que trae.** Es lo que la persona acaba de
   *   escribir y el servidor devolvió con el rechazo;
   * · 🔴 **y si lo rellenó el navegador, no se toca.** Escribir encima le
   *   quita al gestor de contraseñas también la contraseña que había puesto
   *   —las pone en pareja—, y quien entra con su gestor se quedaría con el
   *   segundo campo vacío sin entender por qué.
   */
  useEffect(() => {
    const recordado = leerUsuarioRecordado();
    if (recordado === '') return;

    // La casilla dice la verdad de lo que hay guardado.
    const casilla = casillaRecordar.current;
    if (casilla !== null) casilla.checked = true;

    const campo = campoDocumento.current;
    if (campo === null) return;
    if (campo.value !== '' || loRellenoElNavegador(campo)) return;
    campo.value = recordado;
  }, []);
  const invalido = new Set(rechazo?.campos ?? []);

  /*
    El movimiento sale de los tokens del sistema, no de los valores por defecto
    de la biblioteca: 260 ms para lo que entra y 200 para lo que sale, con las
    dos curvas que el sistema declara — lo que entra desacelera, lo que sale
    acelera.

    Con `prefers-reduced-motion` todo cae a cero. No es cortesía: hay gente para
    quien una transición mal puesta produce mareo. La hoja de tokens ya reduce
    sus duraciones a 1 ms; esto hace lo propio del lado de JavaScript, donde la
    hoja no alcanza.
  */
  const entra = sinMovimiento
    ? { duration: 0 }
    : { duration: 0.26, ease: [0.25, 1, 0.5, 1] as const };
  const sale = sinMovimiento ? { duration: 0 } : { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const };

  /** Salida del glifo que se va. Sin clic previo, sin duración: no es un cambio
   *  de estado, es la preferencia llegando. */
  const salidaDelGlifo = conmutado ? sale : { duration: 0 };

  /*
    `LazyMotion` con `domAnimation` en vez del paquete completo, y `m` en vez de
    `motion`.

    NO ES UNA OPTIMIZACIÓN DE MANUAL: está medido. Con `motion` entero la puerta
    pasó de 135.3 a 175.5 KB comprimidos —97 % de un presupuesto de 180—, y esta
    pantalla tiene UNA sola animación de verdad. `domAnimation` trae lo que aquí
    se usa —animación, presencia, gestos de puntero— y deja fuera el resto.

    `strict` convierte en error de ejecución usar `motion` en vez de `m` dentro
    del árbol: sin eso, el primer componente que se escriba con `motion` vuelve
    a cargar el paquete entero y nadie se entera hasta que la compuerta del
    presupuesto lo dice.
  */
  return (
    <LazyMotion features={domAnimation} strict>
      <div className="acceso">
        <header className="acceso-cabecera acceso-entra acceso-entra--quieto" style={orden(0)}>
          <img
            className="acceso-logo acceso-logo--claro"
            src={logoClaro}
            alt={t('acceso.logo')}
            height={28}
          />
          <img
            className="acceso-logo acceso-logo--oscuro"
            src={logoOscuro}
            alt={t('acceso.logo')}
            height={28}
          />

          {/* Los dos conmutadores de presentación viven aquí y solo aquí. Dentro
            del producto vuelven, pero bajo el avatar de la persona; en la puerta
            no hay avatar todavía, y quien entra de noche no debería aguantar una
            pantalla blanca hasta después de identificarse. */}
          <div className="acceso-controles">
            <button
              type="button"
              className="nat-btn nat-btn--anillo"
              onClick={() => {
                setConmutado(true);
                alternarVidrio();
              }}
              aria-pressed={!vidrio}
              aria-label={t(vidrio ? 'acceso.vidrio.aSolido' : 'acceso.vidrio.aVidrio')}
            >
              {/* `AnimatePresence` con `initial={false}` NO es decoración: sin
                ella el glifo se rendía en el servidor con el estado inicial
                puesto —`opacity:0`— y, si el guion no llegaba, se quedaba
                invisible para siempre. Los dos botones salían vacíos. Medido
                con el navegador sin JavaScript, y es el mismo error de fondo
                que hizo que la entrada de la escena se hiciera en CSS.

                Con `initial={false}`, lo que ya está al primer render aparece
                sin animar; lo que llega después —al pulsar— sí gira. */}
              <AnimatePresence initial={false} mode="wait">
                <m.svg
                  key={vidrio ? 'vidrio' : 'solido'}
                  className="nat-ico nat-ico--20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  initial={conmutado ? { opacity: 0, scale: 0.7 } : false}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.7, transition: salidaDelGlifo }}
                  transition={entra}
                >
                  {vidrio ? GLIFOS.vidrio : GLIFOS.solido}
                </m.svg>
              </AnimatePresence>
            </button>

            <button
              type="button"
              className="nat-btn nat-btn--anillo"
              onClick={() => {
                setConmutado(true);
                alternarTema();
              }}
              aria-pressed={tema === 'oscuro'}
              aria-label={t(tema === 'claro' ? 'acceso.tema.aOscuro' : 'acceso.tema.aClaro')}
            >
              {/* El glifo GIRA al cambiar. Es lo que hace que se lea como un
                conmutador y no como dos botones distintos que se turnan. Y va
                envuelto por el mismo motivo que el de al lado: el estado
                inicial no puede llegar puesto desde el servidor. */}
              <AnimatePresence initial={false} mode="wait">
                <m.svg
                  key={tema}
                  className="nat-ico nat-ico--20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  initial={conmutado ? { opacity: 0, rotate: -70, scale: 0.7 } : false}
                  animate={{ opacity: 1, rotate: 0, scale: 1 }}
                  exit={{ opacity: 0, rotate: 70, scale: 0.7, transition: salidaDelGlifo }}
                  transition={entra}
                >
                  {tema === 'claro' ? GLIFOS.luna : GLIFOS.sol}
                </m.svg>
              </AnimatePresence>
            </button>
          </div>
        </header>

        <section className="acceso-marca">
          <div className="acceso-discurso">
            <h1 className="nat-display acceso-titular acceso-entra" style={orden(0)}>
              <Texto clave="acceso.marca.titular" />
            </h1>

            {/* La ENTRADA que sigue al titular. Existía la clase en el sistema
              —`.nat-lead`, con su talla fluida y su interlineado abierto— y no
              la consumía nadie. El titular dice el porqué del sistema y esto
              dice de dónde sale; los tres puntos de abajo, cómo. */}
            <p className="nat-lead acceso-entrada acceso-entra" style={orden(1)}>
              <Texto clave="acceso.marca.entrada" />
            </p>

            <ul className="acceso-puntos">
              {PUNTOS.map(({ clave, color }, i) => (
                <li className="acceso-punto acceso-entra" key={clave} style={orden(i + 2)}>
                  {/* La VIÑETA de la marca. La forma la fija el sistema
                    —cuadrado inclinado 20 grados, la misma que usa el acceso de
                    Natclar AI— y aquí solo se le da color y sitio.

                    Se dibuja con `transform`, sin activo: antes era una máscara
                    SVG sobre el trazo del símbolo, que es otra pieza de la
                    identidad y tiene otra geometría. */}
                  <span className="nat-vineta acceso-vineta" style={{ color }} aria-hidden="true" />
                  <p className="nat-cuerpo acceso-punto-titulo">
                    <Texto clave={`acceso.marca.${clave}.titulo`} />
                  </p>
                  <p className="nat-apoyo acceso-punto-detalle">
                    <Texto clave={`acceso.marca.${clave}.detalle`} />
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <main className="acceso-puerta">
          <div className="nat-panel acceso-tarjeta acceso-entra acceso-entra--quieto">
            <header className="acceso-encabezado">
              <p className="nat-eyebrow">
                <Texto clave="acceso.formulario.rotulo" />
              </p>
              <h2 className="nat-rotulo acceso-titulo">
                <Texto clave="acceso.formulario.titulo" />
              </h2>
              <p className="nat-cuerpo">
                <Texto clave="acceso.formulario.explicacion" />
              </p>
            </header>

            {/*
            El aviso entra y sale animado, y la ALTURA se anima con él para que
            el formulario no dé un salto: sin eso el bloque aparece de golpe y
            empuja los campos hacia abajo justo cuando la persona está mirando
            dónde se equivocó.

            `initial={false}` es la parte que importa: un aviso que llega YA
            RENDERIZADO del servidor —el caso sin JavaScript, y el de recargar
            con un error— no se anima. Solo se anima el que aparece como
            respuesta a un envío, que es cuando el movimiento significa algo.

            `mode="wait"` para que al cambiar de mensaje salga uno antes de
            entrar el otro, en vez de solaparse a media altura.
          */}
            <AnimatePresence initial={false} mode="wait">
              {mensaje !== null ? (
                <m.div
                  key={mensaje}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0, transition: sale }}
                  transition={entra}
                  style={{ overflow: 'hidden' }}
                >
                  <p className="nat-mensaje nat-mensaje--alerta" role="alert" id={idMensaje}>
                    <svg className="nat-pic" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V8h2v5z" />
                    </svg>
                    <span>{t(mensaje)}</span>
                  </p>
                </m.div>
              ) : null}
            </AnimatePresence>

            {/* EL SEGUNDO ENVÍO SE BLOQUEA AQUÍ, en el formulario, y no en el
              botón. Un `disabled` le quitaría el foco a quien acaba de pulsar,
              y `pointer-events:none` solo cierra la puerta del ratón: con el
              botón enfocado, Intro vuelve a enviar.

              Cada envío es una verificación de credenciales contra el servicio
              de identidad, y ahí es donde viven los contadores de bloqueo por
              intentos fallidos: dos envíos por un doble clic pueden gastarle a
              alguien un intento que no pidió. */}
            <Form
              method="post"
              className="acceso-formulario"
              noValidate
              onSubmit={(evento) => {
                if (enviando) {
                  evento.preventDefault();
                  return;
                }
                /*
                  SE RECUERDA AL ENVIAR, Y SE LEE DEL PROPIO FORMULARIO.

                  ⚠️ **`FormData` y no el valor del campo**: es exactamente lo que
                  se va a enviar, y además es lo único que sirve cuando el
                  navegador rellenó el campo —en ese estado `input.value`
                  devuelve la cadena vacía, medido el 23/09/2026—.

                  Se recuerda lo enviado aunque el servidor acabe rechazando:
                  lo que se guarda es el usuario, no un acceso conseguido, y
                  quien se equivocó de contraseña no tiene por qué reescribir su
                  documento.
                */
                const documento = new FormData(evento.currentTarget).get(CAMPO_DOCUMENTO);
                if (casillaRecordar.current?.checked === true) {
                  recordarUsuario(typeof documento === 'string' ? documento : '');
                } else {
                  olvidarUsuario();
                }
              }}
            >
              {destino === null ? null : (
                <input type="hidden" name={CAMPO_DESTINO} value={destino} />
              )}

              <div className="nat-campo">
                {/* LA FILA DEL RÓTULO: la etiqueta a la izquierda y «Recordar
                  usuario» a la derecha, que es donde la puso Alex en la
                  referencia —y donde significa lo que dice—. Puesta encima del
                  botón se leería como «recordarme», que es mantener la sesión, y
                  eso es justo lo que esta puerta NO hace.

                  Son dos `label` HERMANAS y no una anidada: la casilla dentro
                  del rótulo del campo haría que marcarla enfocara el campo. */}
                <div className="acceso-rotulo">
                  <label className="nat-etiqueta" htmlFor={idDocumento}>
                    <Texto clave="acceso.documento.etiqueta" />
                  </label>
                  {/* SIN `name`, y es la mitad de la decisión: sin nombre no
                    viaja en el envío. El servidor recibe hoy exactamente lo
                    mismo que recibía antes de que esto existiera. */}
                  <label className="acceso-recordar">
                    <input
                      ref={casillaRecordar}
                      type="checkbox"
                      className="nat-casilla"
                      onChange={(evento) => {
                        // Quitar la marca ES el gesto de borrar: no espera a
                        // ningún envío, que puede no llegar nunca.
                        if (!evento.target.checked) olvidarUsuario();
                      }}
                    />
                    <span className="acceso-recordar__texto">
                      <Texto clave="acceso.recordar" />
                    </span>
                  </label>
                </div>
                <input
                  ref={campoDocumento}
                  className="nat-entrada"
                  id={idDocumento}
                  name={CAMPO_DOCUMENTO}
                  type="text"
                  inputMode="numeric"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  maxLength={DOCUMENTO_MAXIMO}
                  placeholder={t('acceso.documento.sugerencia')}
                  defaultValue={rechazo?.documento ?? ''}
                  aria-invalid={invalido.has(CAMPO_DOCUMENTO)}
                  {...(rechazo ? { 'aria-describedby': idMensaje } : {})}
                />
                {/* SIN `autofocus`, y es deliberado. En una página de acceso suele
                  admitirse, pero aquí el panel de marca va ANTES en el
                  documento: enfocar el campo al cargar se saltaría el titular
                  para quien usa lector de pantalla. El coste es un tabulador. */}
              </div>

              <div className="nat-campo">
                <label className="nat-etiqueta" htmlFor={idClave}>
                  <Texto clave="acceso.clave.etiqueta" />
                </label>
                <div className="nat-campo__caja">
                  {/* Sin `defaultValue`: la contraseña NO vuelve del servidor
                    nunca, ni siquiera para comodidad de quien se equivocó. */}
                  <input
                    className="nat-entrada"
                    id={idClave}
                    name={CAMPO_CLAVE}
                    type={claveVisible ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('acceso.clave.sugerencia')}
                    required
                    maxLength={128}
                    aria-invalid={invalido.has(CAMPO_CLAVE)}
                    {...(rechazo ? { 'aria-describedby': idMensaje } : {})}
                  />
                  <button
                    className="nat-campo__accion"
                    type="button"
                    onClick={() => {
                      setClaveVisible((visible) => !visible);
                    }}
                    aria-pressed={claveVisible}
                    aria-controls={idClave}
                    aria-label={t(claveVisible ? 'acceso.clave.ocultar' : 'acceso.clave.mostrar')}
                  >
                    <svg className="nat-ico" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
                      <circle cx="12" cy="12" r="2.6" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* EL BOTÓN SE PONE A TRABAJAR, y hasta ahora solo cambiaba de
                texto. Dos cosas faltaban, y las dos importan en una pantalla
                de acceso:

                · `aria-busy` — sin él, quien usa lector de pantalla pulsa y no
                  se entera de nada: el texto del botón cambia, pero nada
                  anuncia el cambio.
                · No se podía pulsar dos veces, pero SÍ se podía intentar. Cada
                  intento es una verificación de credenciales contra el
                  servicio de identidad, y ahí es donde viven los contadores de
                  bloqueo por intentos fallidos.

                No lleva `disabled`: un control deshabilitado sale del orden de
                tabulación, y quien lo tenía enfocado al pulsar se quedaría sin
                foco a media operación. Lo neutraliza `pointer-events` desde el
                sistema, que además conserva el color — deshabilitado y
                trabajando no dicen lo mismo. */}
              <button
                className="nat-btn nat-btn--texto nat-btn--principal acceso-enviar"
                type="submit"
                aria-busy={enviando}
              >
                {enviando ? <span className="nat-hilando" aria-hidden="true" /> : null}
                <Texto clave={enviando ? 'acceso.entrando' : 'acceso.entrar'} />
              </button>
            </Form>

            <p className="nat-meta acceso-separador">
              <span>
                <Texto clave="acceso.sso.separador" />
              </span>
            </p>

            <button
              className="nat-btn nat-btn--texto nat-btn--anillo acceso-enviar"
              type="button"
              disabled
            >
              <Texto clave="acceso.sso.corporativo" />
            </button>

            <p className="nat-apoyo acceso-legal">
              <Texto clave="acceso.soporte" />
            </p>
          </div>
        </main>

        {/* Los dos datos juntos: entre dos versiones publicadas hay muchos
          artefactos con el mismo número, y la pregunta que se hace de verdad
          —«¿está desplegado mi cambio?»— la responde el commit. */}
        <p className="nat-meta acceso-pie">
          <span>{formatearBuildInfo(build)}</span>
          <span aria-hidden="true">·</span>
          <span>{config.entorno}</span>
        </p>
      </div>
    </LazyMotion>
  );
}
